import axios from 'axios';
import { getUserId } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Chat API
export const chatApi = {
    sendMessage: async (category, message, conversationId = null, sessionMessages = []) => {
        const response = await api.post('/chat', {
            user_id: getUserId(),
            category,
            message,
            conversation_id: conversationId,
            messages: sessionMessages  // Send session history for context (not saved on server)
        });
        return response.data;
    },
    
    // Streaming chat - returns async generator for real-time response
    streamMessage: async function* (category, message, sessionMessages = [], onChunk) {
        const response = await fetch(`${API}/chat/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                user_id: getUserId(),
                category,
                message,
                messages: sessionMessages
            })
        });
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            
            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6));
                        if (data.error) {
                            throw new Error(data.error);
                        }
                        if (onChunk) {
                            onChunk(data);
                        }
                        yield data;
                    } catch (e) {
                        // Skip invalid JSON
                    }
                }
            }
        }
    }
    // REMOVED: getConversations, getConversation, deleteConversation
    // Privacy by design: conversations are NOT stored on server
};

// Journal API
export const journalApi = {
    createEntry: async (title, content, mood = null, tags = []) => {
        const response = await api.post('/journal', {
            user_id: getUserId(),
            title,
            content,
            mood,
            tags
        });
        return response.data;
    },
    
    getEntries: async () => {
        const response = await api.get(`/journal/${getUserId()}`);
        return response.data;
    },
    
    getEntry: async (entryId) => {
        const response = await api.get(`/journal/entry/${entryId}?user_id=${getUserId()}`);
        return response.data;
    },
    
    updateEntry: async (entryId, data) => {
        const response = await api.put(`/journal/entry/${entryId}?user_id=${getUserId()}`, data);
        return response.data;
    },
    
    deleteEntry: async (entryId) => {
        const response = await api.delete(`/journal/entry/${entryId}?user_id=${getUserId()}`);
        return response.data;
    }
};

// Exercises API
export const exercisesApi = {
    getAll: async (category = null) => {
        const params = category ? `?category=${category}` : '';
        const response = await api.get(`/exercises${params}`);
        return response.data;
    },
    
    getById: async (exerciseId) => {
        const response = await api.get(`/exercises/${exerciseId}`);
        return response.data;
    }
};

// TTS API
export const ttsApi = {
    generateSpeech: async (text, voice = 'nova') => {
        const response = await api.post(`/tts?text=${encodeURIComponent(text)}&voice=${voice}`);
        return response.data;
    }
};

// Access Code API
export const accessCodeApi = {
    validate: async (code) => {
        const response = await api.post('/access-code/validate', { code });
        return response.data;
    },
    
    use: async (code) => {
        const response = await api.post('/access-code/use', { code });
        return response.data;
    },
    
    check: async (code) => {
        const response = await api.get(`/access-code/check/${code}`);
        return response.data;
    }
};

export default api;
